import { useCallback, useRef } from 'react';

/**
 * 목표 요소가 제자리에 올 때까지 **따라 올라가는** 스크롤.
 *
 * 두 군데서 쓴다:
 *  - `useScrollToReading` — 해석 본문 첫 줄을 화면 맨 위로
 *  - `useScrollToCta`     — 카드를 다 뽑았을 때 [해석 듣기]를 화면 한가운데로
 *
 * 왜 필요한가: 둘 다 **카드 아래**에 붙는다. 모바일에서는 카드가 화면을 다 채워서, 아래에
 * 무언가 나타나도 사용자는 그걸 못 본 채 카드만 보고 있게 된다. 그냥 맨 위로 올리는 걸로는
 * 안 된다 — 맨 위에는 카드가 있다.
 *
 * ## 한 번만 스크롤하면 안 되는 이유 (실측)
 *
 * 본문이 막 붙은 순간에는 글이 한 줄도 없어 **문서가 화면보다 겨우 조금 길다.** 그래서
 * `scrollIntoView`를 불러도 끝까지 올라가지 못하고 중간에 멈춘다(scrollY 89에서 한계).
 * 글이 흘러 문서가 자라야 비로소 맨 위에 닿는다. 그래서 자라는 동안 몇 번 더 따라 올린다.
 * 카드를 다 뽑은 직후도 마찬가지다 — 카드 배치 애니메이션이 아직 끝나지 않아 목표가 움직인다.
 *
 * ## 사용자와 싸우지 않기
 *
 * 사용자가 카드를 보려고 위로 올리면 **즉시 그만둔다.** 그 판별을 위해 우리 스크롤은
 * 부드럽게(smooth)가 아니라 **즉시** 옮긴다 — 부드러운 스크롤은 진행 중 위치가 계속 바뀌어
 * 사용자가 움직인 것과 구분할 수 없다. 우리가 설정한 값과 실제 값이 어긋나면 사람이 만진 것이다.
 */

/**
 * 해석 본문을 따라 올리는 총 시간. 글이 흐르는 동안 문서가 계속 자라므로 스트리밍이 끝날
 * 때까지는 잡아야 한다 (4초로 뒀더니 다 못 올라가고 중간에서 끝났다 — 실측).
 * 다 올라간 뒤의 호출은 스크롤이 더 안 되므로 아무 일도 하지 않는다.
 */
const READING_BUDGET_MS = 15_000;
/**
 * 버튼을 가운데로 옮기는 총 시간. 본문과 달리 문서가 자라지 않으니 길 이유가 없다.
 * 다만 카드 배치 애니메이션이 끝날 때까지는 목표가 흔들려서, 그보다는 넉넉히 잡는다.
 */
const CTA_BUDGET_MS = 3_000;
/**
 * 남은 거리가 1/e로 줄어드는 데 걸리는 시간. 500px 남짓이면 약 0.9초에 걸쳐 미끄러진다 —
 * 스냅처럼 튀지 않으면서 답답하지도 않다.
 *
 * 예전에는 250ms마다 **남은 거리를 통째로** 뛰었는데, 문서가 아직 짧아 브라우저가 스크롤을
 * 잘라내는 구간에서 이게 계단처럼 툭툭 끊겨 보였다(4~5번의 큰 점프).
 *
 * ⚠️ 프레임당 비율이 아니라 **경과 시간**으로 계산할 것. 비율로 두면 120Hz 화면에서 60Hz의
 * 두 배 속도로 움직인다 — 기기마다 다른 속도가 된다.
 */
const FOLLOW_TAU_MS = 180;
/** 초당 최소 이동 — 끝에서 한없이 느려지는 걸 막는다 */
const FOLLOW_MIN_PX_PER_S = 120;
/** 이 정도면 "제자리에 왔다"고 본다 */
const TOLERANCE_PX = 8;
/** 요소가 화면의 이 비율보다 크면 가운데 맞추기를 포기한다 (위쪽이 잘려 나간다) */
const TALL_RATIO = 0.8;
/** 그렇게 큰 요소는 화면 위에서 이만큼 내려온 자리에 둔다 */
const TALL_TOP_RATIO = 0.15;

type Align = 'top' | 'center';

/** 목표까지 남은 거리(부호 있음) — 양수면 아래로, 음수면 위로 가야 한다 */
function distanceTo(node: HTMLElement, align: Align): number {
  const rect = node.getBoundingClientRect();
  if (align === 'top') return rect.top;
  const vh = window.innerHeight;
  // 화면보다 큰 요소를 가운데 맞추면 위쪽이 화면 밖으로 나간다 — 그때는 위쪽을 기준으로
  if (rect.height > vh * TALL_RATIO) return rect.top - vh * TALL_TOP_RATIO;
  return rect.top + rect.height / 2 - vh / 2;
}

/**
 * ref 콜백을 돌려준다. 요소가 붙으면 따라가기 시작하고, 떨어지면(언마운트) 다시 준비한다
 * — 다시 뽑기·재시도로 같은 요소가 또 나타나면 그때 한 번 더 옮겨야 하기 때문이다.
 */
function useFollowScroll(
  align: Align,
  budgetMs: number,
  /**
   * 제자리에 닿아도 예산이 끝날 때까지 계속 지켜볼지.
   *
   * 카드를 다 뽑은 직후에는 **닿은 뒤에도 위쪽 카드 배치가 마무리되며 버튼이 밀린다**
   * (실측: 다 맞춰 놓고도 22px 어긋난 채 끝났다). 닿자마자 멈추면 그 밀림을 못 잡는다.
   * 해석 본문은 반대다 — 맨 위에 닿으면 그걸로 끝이고, 계속 붙들면 읽는 중에 방해가 된다.
   */
  settle: boolean,
): (node: HTMLElement | null) => void {
  const done = useRef(false);

  return useCallback(
    (node: HTMLElement | null) => {
      if (!node) {
        done.current = false; // 언마운트 = 다음 차례를 위해 되돌린다
        return;
      }
      if (done.current) return;
      done.current = true;

      const startedAt = performance.now();
      let lastAt = startedAt;
      let expected = window.scrollY;
      let raf = 0;

      const stop = () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('scroll', onScroll);
      };
      function onScroll() {
        // 우리가 옮긴 위치와 다르면 사람이 만진 것 — 더 따라가지 않는다
        if (Math.abs(window.scrollY - expected) > TOLERANCE_PX) stop();
      }

      const follow = () => {
        const now = performance.now();
        if (now - startedAt > budgetMs) return stop();
        const remaining = distanceTo(node, align);
        /**
         * 맨 위 정렬은 **지나친 경우(음수)에도 멈춘다.** 되돌리려고 아래로 끌어내리면
         * 글을 읽는 중에 화면이 거꾸로 움직인다. 가운데 정렬만 양쪽으로 맞춘다.
         */
        const reached =
          align === 'top' ? remaining <= TOLERANCE_PX : Math.abs(remaining) <= TOLERANCE_PX;
        if (reached) {
          if (!settle) return stop();
          // 닿았으니 이번 프레임은 움직이지 않는다 — 1px씩 떠는 걸 막는다
          lastAt = now;
          raf = requestAnimationFrame(follow);
          return;
        }
        // 지난 프레임 이후 흐른 시간만큼만 좁힌다 — 화면 주사율이 달라도 같은 속도로 움직인다
        const dt = Math.min(now - lastAt, 100); // 탭이 잠들었다 깨면 한 번에 튀지 않게
        lastAt = now;
        // 즉시 — smooth면 진행 중 위치가 계속 바뀌어 사용자 조작과 구분이 안 된다.
        // 대신 매 프레임 조금씩 옮겨 부드러움을 만든다.
        const left = Math.abs(remaining);
        const step = Math.min(
          left, // 목표를 지나치지 않는다 — 지나치면 가운데 정렬이 좌우로 떤다
          Math.max((FOLLOW_MIN_PX_PER_S * dt) / 1000, left * (1 - Math.exp(-dt / FOLLOW_TAU_MS))),
        );
        window.scrollTo({ top: Math.round(window.scrollY + Math.sign(remaining) * step) });
        /**
         * 요청한 값이 아니라 **실제로 간 위치**를 기억한다. 문서가 아직 짧으면 브라우저가
         * 스크롤을 잘라내는데, 요청값과 비교하면 그 차이를 사용자 조작으로 오해해 곧바로
         * 멈춰 버린다 (실측: 455를 요청했는데 89까지만 갔다).
         */
        expected = window.scrollY;
        raf = requestAnimationFrame(follow);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      // 두 번 기다린다 — 첫 프레임에는 레이아웃이 아직 잡히지 않아 엉뚱한 위치를 잰다
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(follow);
      });
    },
    [align, budgetMs, settle],
  );
}

/** 해석 본문 첫 줄이 화면 맨 위에 오도록 */
export function useScrollToReading(): (node: HTMLElement | null) => void {
  return useFollowScroll('top', READING_BUDGET_MS, false);
}

/**
 * 카드를 다 뽑았을 때 [해석 듣기]가 화면 한가운데 오도록.
 * 마지막 카드를 놓은 자리와 버튼 사이가 멀어서, 다 뽑고도 뭘 눌러야 할지 못 찾는다.
 */
export function useScrollToCta(): (node: HTMLElement | null) => void {
  return useFollowScroll('center', CTA_BUDGET_MS, true);
}
